// src/admin/pages/Volunteers.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import FilterListIcon from '@mui/icons-material/FilterList';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import CrudTable from '../components/CrudTable';
import { adminVolunteers, adminSendEmail } from '../../utils/api';

// PDF helpers
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/* -----------------------------------------------------------
   Twilio client-side guards
------------------------------------------------------------ */
const TWILIO_MAX_BODY = 1600;
const TWILIO_MAX_MEDIA = 10;
const TWILIO_MAX_MEDIA_TOTAL_BYTES = 5 * 1024 * 1024;

const b = (v) => v === 1 || v === '1' || v === true || String(v).toLowerCase() === 'true';
const getFri = (row) => b(row.available_fri) || b(row.available_fi);

/* Days (used by both page and filter dialog) */
const DAYS = [
    { key: 'mon', label: 'Mon' },
    { key: 'tue', label: 'Tue' },
    { key: 'wed', label: 'Wed' },
    { key: 'thu', label: 'Thu' },
    { key: 'fri', label: 'Fri' },
    { key: 'sat', label: 'Sat' },
    { key: 'sun', label: 'Sun' },
];

/* Build an empty advanced filter object (used in page + group dialog) */
function emptyAdvFilters() {
    return {
        anyMorning: false,
        anyAfternoon: false,
        slots: Object.fromEntries(
            DAYS.flatMap((d) => [
                [`${d.key}_morning`, false],
                [`${d.key}_afternoon`, false],
            ])
        ),
        interests: {
            open_roberts_house: false,
            open_depot_museum: false,
            open_caboose: false,
            front_desk: false,
            collections: false,
            research: false,
            programs: false,
            other: false,
        },
    };
}

/* -------------------------------- Helpers / small components -------------------------------- */

function AvailabilityCapsules({ row, compact = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const morning = b(row.time_morning);
    const afternoon = b(row.time_afternoon);

    const slots = DAYS.flatMap((d) => {
        const m = b(row[`available_${d.key}_morning`]);
        const a = b(row[`available_${d.key}_afternoon`]);
        const items = [];
        if (m) items.push(`${d.label} AM`);
        if (a) items.push(`${d.label} PM`);
        return items;
    });

    if (!morning && !afternoon && slots.length === 0) {
        return <Typography variant="body2">—</Typography>;
    }

    if (compact) {
        return (
            <Typography
                variant="body2"
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                }}
            >
                {morning && (
                    <Chip
                        label="AM"
                        size="small"
                        color="primary"
                        sx={{ borderRadius: 999, fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    />
                )}
                {afternoon && (
                    <Chip
                        label="PM"
                        size="small"
                        color="primary"
                        sx={{ borderRadius: 999, fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    />
                )}
                {slots.map((s) => (
                    <Chip
                        key={s}
                        label={s}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderRadius: 999,
                            fontSize: isMobile ? '0.7rem' : '0.75rem',
                        }}
                    />
                ))}
            </Typography>
        );
    }

    return (
        <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.5}>
                {morning && <Chip label="AM" size="small" color="primary" />}
                {afternoon && <Chip label="PM" size="small" color="primary" />}
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {slots.map((s) => (
                    <Chip key={s} label={s} size="small" variant="outlined" />
                ))}
            </Stack>
        </Stack>
    );
}

function InterestsList({ row }) {
    const entries = [
        ['open_roberts_house', 'Roberts House'],
        ['open_depot_museum', 'Depot Museum'],
        ['open_caboose', 'Caboose'],
        ['front_desk', 'Front desk / Admissions'],
        ['collections', 'Collections / Archives'],
        ['research', 'Research'],
        ['programs', 'Programs & Tours'],
        ['other', 'Other'],
    ];

    const active = entries.filter(([key]) => b(row[key]));

    if (!active.length) return <Typography variant="body2">—</Typography>;

    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {active.map(([key, label]) => (
                <Chip key={key} label={label} size="small" variant="outlined" />
            ))}
        </Stack>
    );
}

function InfoField({ label, value }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="overline" sx={{ lineHeight: 1 }}>
                {label}
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {value ?? '—'}
            </Typography>
        </Box>
    );
}

function InfoNote({ label, value }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="overline" sx={{ lineHeight: 1 }}>
                {label}
            </Typography>
            <Box
                sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    whiteSpace: 'pre-wrap',
                    minHeight: 100,
                }}
            >
                {value || '—'}
            </Box>
        </Box>
    );
}

/* -------------------------------- Detail dialog -------------------------------- */

function VolunteerDetailDialog({ open, onClose, row }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!row) return null;

    const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—';

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose?.();
            }}
        >
            <DialogTitle>Volunteer Details</DialogTitle>
            <DialogContent dividers sx={{ maxHeight: '80vh' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={1.5}>
                            <InfoField label="Name" value={fullName} />
                            <InfoField label="Email" value={row.email} />
                            <InfoField label="Phone" value={row.phone_nbr} />
                            <InfoField label="City" value={row.city} />
                            <InfoField label="Preferred contact" value={row.preferred_contact} />
                            <InfoNote label="Skills / Background" value={row.skills} />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={1.5}>
                            <InfoField
                                label="General availability"
                                value={
                                    b(row.time_morning) || b(row.time_afternoon)
                                        ? `${b(row.time_morning) ? 'AM' : ''}${
                                            b(row.time_morning) && b(row.time_afternoon) ? ' & ' : ''
                                        }${b(row.time_afternoon) ? 'PM' : ''}`
                                        : '—'
                                }
                            />
                            <Box>
                                <Typography variant="overline" sx={{ lineHeight: 1 }}>
                                    Weekly schedule
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <AvailabilityCapsules row={row} />
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="overline" sx={{ lineHeight: 1 }}>
                                    Interests
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <InterestsList row={row} />
                                </Box>
                            </Box>
                            <InfoNote label="Notes" value={row.notes} />
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

/* -------------------------------- Email dialog -------------------------------- */

function EmailDialog({ open, onClose, name, email, onSent }) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setSubject('');
            setMessage('');
        }
    }, [open, name, email]);

    const send = async () => {
        if (!email) return alert('This volunteer does not have an email address yet.');
        if (!subject.trim() || !message.trim()) return;

        setBusy(true);
        try {
            const res = await adminSendEmail({ to: email, subject, message });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to send message.');
            onSent?.();
            onClose?.();
        } catch (e) {
            alert(e.message || 'Failed to send message.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Send Email</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                <InfoField label="To" value={`${name || '—'} <${email || '—'}>`} />
                <TextField
                    label="Subject"
                    fullWidth
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                />
                <TextField
                    label="Message"
                    fullWidth
                    multiline
                    minRows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={send} disabled={busy || !subject.trim() || !message.trim()}>
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* -------------------------------- Group / messaging dialogs -------------------------------- */

function MessageGroupDialog({ open, onClose, group, onSent }) {
    const [body, setBody] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setBody('');
        }
    }, [open, group?.id]);

    const send = async () => {
        if (!group) return;
        if (!body.trim()) return;
        if (body.length > TWILIO_MAX_BODY) {
            alert(`Message is too long (max ${TWILIO_MAX_BODY} characters).`);
            return;
        }

        setBusy(true);
        try {
            const res = await fetch(`/api/admin/groups/${group.id}/message`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ body }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to send message.');
            onSent?.();
            onClose?.();
        } catch (e) {
            alert(e.message || 'Failed to send message.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Message Group</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                <InfoField label="Group" value={group?.name || '—'} />
                <TextField
                    label="Text message"
                    fullWidth
                    multiline
                    minRows={5}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    helperText={`${body.length}/${TWILIO_MAX_BODY} characters`}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={send}
                    disabled={busy || !body.trim()}
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function EditGroupDialog({ open, onClose, mode, group, onSaved, allVolunteers }) {
    const [name, setName] = useState('');
    const [members, setMembers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [localAdv, setLocalAdv] = useState(emptyAdvFilters());
    const [localFilterOpen, setLocalFilterOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (open && group) {
            setName(group.name || '');
            setMembers(group.members || []);
        }
    }, [open, group?.id]);

    const addVolunteer = (row) => {
        if (members.some((m) => m.id === row.id)) return;
        setMembers((prev) => [...prev, row]);
    };
    const removeVolunteer = (id) => {
        setMembers((prev) => prev.filter((m) => m.id !== id));
    };

    const matchesLocalFilter = (row) => {
        // Reuse advanced filters for this group-level dialog
        return matchesAdvFilters_OR(row, localAdv);
    };

    const available = useMemo(
        () => allVolunteers.filter((v) => !members.some((m) => m.id === v.id) && matchesLocalFilter(v)),
        [allVolunteers, members, localAdv]
    );

    const save = async () => {
        if (!name.trim()) return;
        setBusy(true);

        try {
            const payload = {
                name: name.trim(),
                member_ids: members.map((m) => m.id),
            };
            const url =
                mode === 'edit' && group?.id
                    ? `/api/admin/groups/${group.id}`
                    : '/api/admin/groups';
            const method = mode === 'edit' ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to save group.');
            onSaved?.();
            onClose?.();
        } catch (e) {
            alert(e.message || 'Failed to save group.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
            <DialogTitle>{mode === 'edit' ? 'Edit Group' : 'New Group'}</DialogTitle>
            <DialogContent dividers sx={{ maxHeight: '80vh' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Group name"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Group Members ({members.length})
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                maxHeight: 360,
                                overflowY: 'auto',
                                p: 1,
                            }}
                        >
                            {members.length === 0 ? (
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    No members in this group yet.
                                </Typography>
                            ) : (
                                <Stack spacing={0.5}>
                                    {members.map((m) => (
                                        <Box
                                            key={m.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 0.75,
                                                borderRadius: 1,
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                },
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {`${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {m.email || m.phone_nbr}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => removeVolunteer(m.id)}
                                                aria-label="Remove from group"
                                            >
                                                <RemoveCircleOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">Volunteer Pool</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FilterListIcon />}
                                onClick={() => setLocalFilterOpen(true)}
                            >
                                Filters
                            </Button>
                        </Stack>
                        <Paper
                            variant="outlined"
                            sx={{
                                maxHeight: 360,
                                overflowY: 'auto',
                                p: 1,
                            }}
                        >
                            {available.length === 0 ? (
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    No volunteers match the current filter.
                                </Typography>
                            ) : (
                                <Stack spacing={0.5}>
                                    {available.map((v) => (
                                        <Box
                                            key={v.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 0.75,
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                },
                                            }}
                                            onClick={() => addVolunteer(v)}
                                        >
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {`${v.first_name || ''} ${v.last_name || ''}`.trim() || '—'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {v.email || v.phone_nbr}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    addVolunteer(v);
                                                }}
                                                aria-label="Add to group"
                                            >
                                                <AddCircleOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                            <Typography variant="caption" sx={{ mt: 1, opacity: 0.8 }}>
                                Tip: drag a name to the left to add, or double‑click to add.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={save} disabled={busy || !name.trim() || !members.length}>
                    Save
                </Button>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>

            {/* Local filter dialog for the right-side list */}
            <VolunteerFilterDialog
                open={localFilterOpen}
                initial={localAdv}
                onApply={(next) => {
                    setLocalAdv(next);
                    setLocalFilterOpen(false);
                }}
                onClose={() => setLocalFilterOpen(false)}
                title="Filter Volunteers (for this group)"
            />
        </Dialog>
    );
}

/* ----------------------------------- Filters ----------------------------------- */

function VolunteerFilterDialog({ open, onClose, initial, onApply, title = 'Filters' }) {
    const [anyMorning, setAnyMorning] = useState(false);
    const [anyAfternoon, setAnyAfternoon] = useState(false);
    const [slots, setSlots] = useState({});
    const [interests, setInterests] = useState({});

    useEffect(() => {
        if (open) {
            const base = initial || emptyAdvFilters();
            setAnyMorning(base.anyMorning);
            setAnyAfternoon(base.anyAfternoon);
            setSlots(base.slots);
            setInterests(base.interests);
        }
    }, [open, initial]);

    const toggleSlot = (key) => {
        setSlots((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleInterest = (key) => {
        setInterests((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const apply = () => {
        onApply?.({
            anyMorning,
            anyAfternoon,
            slots,
            interests,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        General availability
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={anyMorning}
                                    onChange={(e) => setAnyMorning(e.target.checked)}
                                />
                            }
                            label="Available some mornings"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={anyAfternoon}
                                    onChange={(e) => setAnyAfternoon(e.target.checked)}
                                />
                            }
                            label="Available some afternoons"
                        />
                    </Stack>
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Weekly schedule
                    </Typography>
                    <Grid container spacing={1}>
                        {DAYS.map((d) => (
                            <Grid key={d.key} item xs={6} sm={3}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {d.label}
                                </Typography>
                                <Stack spacing={0}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!slots[`${d.key}_morning`]}
                                                onChange={() => toggleSlot(`${d.key}_morning`)}
                                            />
                                        }
                                        label="AM"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!slots[`${d.key}_afternoon`]}
                                                onChange={() => toggleSlot(`${d.key}_afternoon`)}
                                            />
                                        }
                                        label="PM"
                                    />
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Interests
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {[
                            ['open_roberts_house', 'Roberts House'],
                            ['open_depot_museum', 'Depot Museum'],
                            ['open_caboose', 'Caboose'],
                            ['front_desk', 'Front desk / Admissions'],
                            ['collections', 'Collections / Archives'],
                            ['research', 'Research'],
                            ['programs', 'Programs & Tours'],
                            ['other', 'Other'],
                        ].map(([key, label]) => (
                            <Chip
                                key={key}
                                label={label}
                                color={interests[key] ? 'primary' : 'default'}
                                variant={interests[key] ? 'filled' : 'outlined'}
                                onClick={() => toggleInterest(key)}
                            />
                        ))}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={apply}
                >
                    Apply Filters
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* -------------- Advanced filter matching (page + group dialog) -------------- */

function matchesAdvFilters_OR(row, adv) {
    if (!adv || (!adv.anyMorning && !adv.anyAfternoon && !Object.values(adv.slots || {}).some(Boolean) && !Object.values(adv.interests || {}).some(Boolean))) {
        return true; // no filters
    }

    // any morning / afternoon
    if (adv.anyMorning && !b(row.time_morning)) return false;
    if (adv.anyAfternoon && !b(row.time_afternoon)) return false;

    // day/time slots
    if (adv.slots) {
        for (const d of DAYS) {
            if (adv.slots[`${d.key}_morning`] && !b(row[`available_${d.key}_morning`])) return false;
            if (adv.slots[`${d.key}_afternoon`] && !b(row[`available_${d.key}_afternoon`])) return false;
        }
    }

    // interests
    if (adv.interests) {
        for (const key of Object.keys(adv.interests)) {
            if (adv.interests[key] && !b(row[key])) return false;
        }
    }

    return true;
}

/* ----------------------------------- Page ----------------------------------- */

export default function VolunteersPage() {
    const [tab, setTab] = useState('volunteers');

    // volunteers (for viewing + right panel of EditGroupDialog)
    const [rows, setRows] = useState([]);

    // Name-only quick filter (apply on Search click)
    const [filters, setFilters] = useState({ q: '' });
    const [nameInput, setNameInput] = useState('');

    // Advanced filters
    const [advFilters, setAdvFilters] = useState(emptyAdvFilters());
    const [filterDlgOpen, setFilterDlgOpen] = useState(false);

    // volunteer detail/email dialogs
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRow, setDetailRow] = useState(null);
    const [emailDlg, setEmailDlg] = useState({ open: false, name: '', email: '' });

    // groups + dialogs
    const [groups, setGroups] = useState([]);
    const [groupDlg, setGroupDlg] = useState({ open: false, group: null });
    const [editDlg, setEditDlg] = useState({ open: false, mode: 'create', group: null });

    // Delete group dialog state
    const [delDlg, setDelDlg] = useState({ open: false, id: null, name: '', busy: false, error: '' });

    // toasts
    const [snackEmail, setSnackEmail] = useState(false);
    const [snackMsg, setSnackMsg] = useState(false);

    // inline banner
    const [banner, setBanner] = useState(null);
    const bannerTimer = useRef(null);
    const flashBanner = (text, type = 'success', ms = 3000) => {
        setBanner({ text, type });
        if (bannerTimer.current) clearTimeout(bannerTimer.current);
        bannerTimer.current = setTimeout(() => setBanner(null), ms);
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Load volunteers
    const loadVolunteers = async () => {
        const data = await adminVolunteers();
        setRows(data.rows || []);
    };
    useEffect(() => {
        loadVolunteers();
    }, []);

    // Load groups only when messaging tab is active
    const loadGroups = async () => {
        const res = await fetch('/api/admin/groups', { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (res.ok) setGroups(data.rows || []);
    };
    useEffect(() => {
        if (tab === 'messaging') loadGroups();
    }, [tab]);

    const openGroupForSend = async (groupId) => {
        const res = await fetch(`/api/admin/groups/${groupId}`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.group) {
            setGroupDlg({ open: true, group: data.group });
        } else {
            flashBanner(data?.error || 'Failed to load group.', 'error', 4500);
        }
    };

    const openGroupForEdit = async (groupId) => {
        const res = await fetch(`/api/admin/groups/${groupId}`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.group) {
            setEditDlg({ open: true, mode: 'edit', group: data.group });
        } else {
            flashBanner(data?.error || 'Failed to load group.', 'error', 4500);
        }
    };

    const askDeleteGroup = (group) => {
        setDelDlg({ open: true, id: group.id, name: group.name, busy: false, error: '' });
    };

    const doDeleteGroup = async () => {
        if (!delDlg.id) return;
        setDelDlg((s) => ({ ...s, busy: true, error: '' }));
        try {
            const res = await fetch(`/api/admin/groups/${delDlg.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to delete group.');
            setDelDlg({ open: false, id: null, name: '', busy: false, error: '' });
            flashBanner('Group deleted.');
            loadGroups();
        } catch (e) {
            setDelDlg((s) => ({ ...s, busy: false, error: e.message || 'Failed to delete group.' }));
        }
    };

    const baseFiltered = useMemo(() => {
        const q = String(filters.q || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const name = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
            return name.includes(q);
        });
    }, [rows, filters]);

    // final filtered with advanced OR logic
    const filtered = useMemo(
        () => baseFiltered.filter((r) => matchesAdvFilters_OR(r, advFilters)),
        [baseFiltered, advFilters]
    );

    // ---------- COLUMNS ----------
    const volunteerColumns = useMemo(
        () => [
            {
                key: 'first_name',
                header: 'Name',
                nowrap: true,
                render: (_v, r) => (
                    <Button
                        variant="text"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setDetailRow(r);
                            setDetailOpen(true);
                        }}
                        sx={isMobile ? { fontSize: '0.95rem' } : undefined}
                    >
                        {`${r.first_name || ''} ${r.last_name || ''}`.trim() || '—'}
                    </Button>
                ),
            },
            {
                key: 'email',
                header: 'Email',
                nowrap: !isMobile,
                render: (v, r) =>
                    v ? (
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<EmailIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                setEmailDlg({
                                    open: true,
                                    name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                                    email: v,
                                });
                            }}
                            sx={
                                isMobile
                                    ? {
                                        p: 0,
                                        minWidth: 0,
                                        textTransform: 'none',
                                        justifyContent: 'flex-start',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-all',
                                        lineHeight: 1.2,
                                        fontSize: '0.85rem',
                                        textAlign: 'left',
                                    }
                                    : undefined
                            }
                        >
                            {v}
                        </Button>
                    ) : (
                        '—'
                    ),
            },
            {
                key: 'phone_nbr',
                header: 'Phone',
                nowrap: !isMobile,
                render: (v) => (
                    <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        sx={isMobile ? { fontSize: '0.9rem' } : undefined}
                    >
                        {v || '—'}
                    </Typography>
                ),
            },
            {
                key: 'city',
                header: 'City',
                nowrap: !isMobile,
                render: (v) => (
                    <Typography
                        variant={isMobile ? 'body2' : 'body1'}
                        sx={isMobile ? { fontSize: '0.9rem' } : undefined}
                    >
                        {v || '—'}
                    </Typography>
                ),
            },
            {
                key: 'time_morning',
                header: 'AM',
                nowrap: true,
                render: (v) => (b(v) ? <CheckIcon fontSize={isMobile ? 'small' : 'medium'} /> : '—'),
            },
            {
                key: 'time_afternoon',
                header: 'PM',
                nowrap: true,
                render: (v) => (b(v) ? <CheckIcon fontSize={isMobile ? 'small' : 'medium'} /> : '—'),
            },
        ],
        [isMobile]
    );

    const groupColumns = [
        {
            key: 'name',
            header: 'Group Name',
            nowrap: true,
            render: (v, r) => (
                <Button size="small" variant="text" onClick={() => openGroupForSend(r.id)}>
                    {v}
                </Button>
            ),
        },
        { key: 'members_count', header: '# People', nowrap: true },
        {
            key: 'created_at',
            header: 'Created',
            nowrap: true,
            render: (v) => (v ? String(v).slice(0, 19).replace('T', ' ') : '—'),
        },
        {
            key: '__actions__',
            header: 'Actions',
            nowrap: true,
            render: (_v, r) => (
                <Stack component="span" direction="row" gap={0.5} sx={{ whiteSpace: 'nowrap', alignItems: 'center' }}>
                    {/* Mobile: icon-only compact; Desktop: labeled buttons */}
                    {isMobile ? (
                        <>
                            <Tooltip title="Edit group">
                                <IconButton size="small" onClick={() => openGroupForEdit(r.id)} aria-label="edit group">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete group">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => askDeleteGroup(r)}
                                    aria-label="delete group"
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => openGroupForEdit(r.id)}
                            >
                                Edit
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => askDeleteGroup(r)}
                            >
                                Delete
                            </Button>
                        </>
                    )}
                </Stack>
            ),
        },
    ];

    // ---------- PDF DOWNLOAD HANDLERS ----------
    const handleDownloadVolunteersPdf = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'pt',
            format: 'a4',
        });

        const title = 'Volunteers Report';
        const generatedAt = new Date().toLocaleString();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text(title, 40, 40);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(80);
        doc.text(`Generated: ${generatedAt}`, 40, 60);
        doc.text(`Total volunteers: ${filtered.length}`, 40, 76);

        if (!filtered.length) {
            doc.setTextColor(120);
            doc.text('No volunteers match the current filters.', 40, 100);
            doc.save('volunteers-report.pdf');
            return;
        }

        const head = [['Name', 'Email', 'Phone', 'City', 'AM', 'PM']];
        const body = filtered.map((row) => {
            const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—';
            const email = row.email || '—';
            const phone = row.phone_nbr || row.phone || '—';
            const city = row.city || '—';
            const am = b(row.time_morning) ? 'Yes' : 'No';
            const pm = b(row.time_afternoon) ? 'Yes' : 'No';
            return [name, email, phone, city, am, pm];
        });

        autoTable(doc, {
            head,
            body,
            startY: 96,
            styles: {
                fontSize: 9,
                cellPadding: 6,
                valign: 'middle',
                overflow: 'linebreak',
            },
            headStyles: {
                fillColor: [33, 150, 243],
                textColor: 255,
                fontSize: 10,
                halign: 'left',
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245],
            },
            bodyStyles: {
                textColor: 40,
            },
            margin: { top: 80, left: 40, right: 40, bottom: 40 },
        });

        doc.save('volunteers-report.pdf');
    };

    const handleDownloadGroupsPdf = () => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'a4',
        });

        const title = 'Volunteer Groups Report';
        const generatedAt = new Date().toLocaleString();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(40);
        doc.text(title, 40, 40);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(80);
        doc.text(`Generated: ${generatedAt}`, 40, 60);
        doc.text(`Total groups: ${groups.length}`, 40, 76);

        if (!groups.length) {
            doc.setTextColor(120);
            doc.text('There are no groups to include in this report.', 40, 100);
            doc.save('volunteer-groups-report.pdf');
            return;
        }

        const head = [['Group Name', '# People', 'Created']];
        const body = groups.map((row) => {
            const name = row.name || '—';
            const members = row.members_count ?? '—';
            const created = row.created_at
                ? String(row.created_at).slice(0, 19).replace('T', ' ')
                : '—';
            return [name, String(members), created];
        });

        autoTable(doc, {
            head,
            body,
            startY: 96,
            styles: {
                fontSize: 9,
                cellPadding: 6,
                valign: 'middle',
                overflow: 'linebreak',
            },
            headStyles: {
                fillColor: [33, 150, 243],
                textColor: 255,
                fontSize: 10,
                halign: 'left',
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245],
            },
            bodyStyles: {
                textColor: 40,
            },
            margin: { top: 80, left: 40, right: 40, bottom: 40 },
        });

        doc.save('volunteer-groups-report.pdf');
    };

    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            <AppBar position="sticky" color="default" elevation={0} sx={{ top: 0, zIndex: 10 }}>
                <Tabs
                    value={tab}
                    onChange={(_e, v) => setTab(v)}
                    textColor="primary"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab value="volunteers" label="Volunteers" />
                    <Tab value="messaging" label="Messaging Center" />
                </Tabs>
            </AppBar>

            {/* ---- VOLUNTEERS TAB ---- */}
            {tab === 'volunteers' && (
                <Box>
                    <Paper
                        elevation={1}
                        sx={{
                            position: 'sticky',
                            top: 48,
                            zIndex: 5,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.25,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Volunteers
                            </Typography>
                            <Stack direction="row" gap={1} alignItems="center">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadVolunteersPdf}
                                >
                                    Download PDF
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<FilterListIcon />}
                                    onClick={() => setFilterDlgOpen(true)}
                                >
                                    Filters
                                </Button>
                            </Stack>
                        </Box>

                        {/* Name-only input with Search/Clear actions (no auto-search) */}
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    size="small"
                                    label="Search by name"
                                    fullWidth
                                    value={nameInput}
                                    onChange={(e) => setNameInput(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" gap={1}>
                                    <Button
                                        size={isMobile ? 'small' : 'medium'}
                                        onClick={() => {
                                            setNameInput('');
                                            setFilters({ q: '' });
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        size={isMobile ? 'small' : 'medium'}
                                        variant="contained"
                                        onClick={() => setFilters({ q: nameInput })}
                                    >
                                        Search
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>

                    <CrudTable
                        ariaLabel="Volunteers list"
                        columns={volunteerColumns}
                        rows={filtered}
                        height="calc(100dvh - 360px)"
                    />

                    {/* dialogs */}
                    <VolunteerDetailDialog open={detailOpen} row={detailRow} onClose={() => setDetailOpen(false)} />
                    <EmailDialog
                        open={emailDlg.open}
                        name={emailDlg.name}
                        email={emailDlg.email}
                        onClose={() => setEmailDlg({ open: false, name: '', email: '' })}
                        onSent={() => setSnackEmail(true)}
                    />

                    {/* FULL filter dialog */}
                    <VolunteerFilterDialog
                        open={filterDlgOpen}
                        initial={advFilters}
                        onApply={(next) => {
                            setAdvFilters(next);
                            setFilterDlgOpen(false);
                        }}
                        onClose={() => setFilterDlgOpen(false)}
                    />
                </Box>
            )}

            {/* ---- MESSAGING CENTER TAB ---- */}
            {tab === 'messaging' && (
                <Box>
                    <Paper elevation={1} sx={{ position: 'sticky', top: 48, zIndex: 5, p: 2 }}>
                        {banner && (
                            <Alert
                                severity={banner.type === 'error' ? 'error' : 'success'}
                                icon={banner.type === 'error' ? undefined : <CheckIcon fontSize="small" />}
                                variant="outlined"
                                sx={{ mb: 1 }}
                            >
                                {banner.text}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Messaging Center
                            </Typography>
                            <Stack direction="row" gap={1} alignItems="center">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadGroupsPdf}
                                    disabled={!groups.length}
                                >
                                    Download PDF
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<GroupAddIcon />}
                                    onClick={() =>
                                        setEditDlg({
                                            open: true,
                                            mode: 'create',
                                            group: { id: null, name: '', members: [] },
                                        })
                                    }
                                >
                                    New Group
                                </Button>
                            </Stack>
                        </Box>
                    </Paper>

                    {groups.length === 0 ? (
                        <Box sx={{ height: 'calc(100dvh - 240px)', display: 'grid', placeItems: 'center' }}>
                            <Typography variant="body1" sx={{ opacity: 0.7 }}>
                                No Groups Made
                            </Typography>
                        </Box>
                    ) : (
                        <CrudTable
                            ariaLabel="Groups list"
                            columns={groupColumns}
                            rows={groups}
                            height="calc(100dvh - 280px)"
                        />
                    )}

                    <MessageGroupDialog
                        open={groupDlg.open}
                        group={groupDlg.group}
                        onClose={() => setGroupDlg({ open: false, group: null })}
                        onSent={() => setSnackMsg(true)}
                    />

                    <EditGroupDialog
                        open={editDlg.open}
                        mode={editDlg.mode}
                        group={editDlg.group}
                        onClose={() => setEditDlg({ open: false, mode: 'create', group: null })}
                        onSaved={loadGroups}
                        allVolunteers={rows}
                    />

                    {/* Delete group dialog */}
                    <Dialog open={delDlg.open} onClose={() => setDelDlg({ open: false, id: null, name: '', busy: false, error: '' })}>
                        <DialogTitle>Delete Group</DialogTitle>
                        <DialogContent dividers>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Are you sure you want to delete the group{' '}
                                <strong>{delDlg.name || 'this group'}</strong>?
                            </Typography>
                            {delDlg.error && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {delDlg.error}
                                </Alert>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() =>
                                    setDelDlg({ open: false, id: null, name: '', busy: false, error: '' })
                                }
                                disabled={delDlg.busy}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={doDeleteGroup}
                                disabled={delDlg.busy}
                                startIcon={<DeleteOutlineIcon />}
                            >
                                Delete
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}

            {/* Snackbars */}
            <Snackbar open={snackEmail} autoHideDuration={3000} onClose={() => setSnackEmail(false)}>
                <Alert severity="success" onClose={() => setSnackEmail(false)}>
                    Message sent.
                </Alert>
            </Snackbar>
            <Snackbar open={snackMsg} autoHideDuration={3000} onClose={() => setSnackMsg(false)}>
                <Alert severity="success" onClose={() => setSnackMsg(false)}>
                    Message sent.
                </Alert>
            </Snackbar>
        </Box>
    );
}
